import type { PropsWithChildren } from "react";
import React, { useMemo, useState } from "react";

import Card from "@busbud/design-system-components/dist/Card";

import { createArrayOfLength } from "@app/utils/array";

import type { ExpandableCardProps as ExpandableCardProps } from "../card-with-header/expandable-card/expandable-card";
import type { ExpandableCardContentProps } from "../card-with-header/expandable-card/expandable-card-content";

import { useStyles } from "./expandable-cards-accordion.styles";

interface Props {
  single_item_opened?: boolean;
  card_classes_override?: ExpandableCardProps["card_classes_override"];
  card_header_classes_override?: string;
}

type ExtendedProps = Props & React.ComponentProps<typeof Card>;

function getInitialExpandedValue(children: React.ReactNode): boolean[] {
  return React.Children.map<boolean, React.ReactNode>(children, child => {
    return (
      React.isValidElement<ExpandableCardContentProps>(child) &&
      child.props.is_expanded === true
    );
  }) as boolean[];
}

export const ExpandableCardsAccordion: React.FC<
  PropsWithChildren<ExtendedProps>
> = ({
  single_item_opened = false,
  children,
  card_classes_override,
  card_header_classes_override,
  ...otherProps
}) => {
  const { cx, classes } = useStyles();
  const initial_expanded_values = useMemo(() => {
    return getInitialExpandedValue(children);
  }, [children]);

  const [expanded_state, setExpandedState] = useState<boolean[]>(
    initial_expanded_values
  );

  const header_classes_override = useMemo(() => {
    return {
      root: cx(classes.header, card_header_classes_override)
    };
  }, [cx, classes, card_header_classes_override]);

  const handleOnClick = (idx: number) => {
    const new_state: boolean[] = single_item_opened
      ? createArrayOfLength(expanded_state.length).map(() => false)
      : [...expanded_state];

    new_state[idx] = !expanded_state[idx];

    setExpandedState(new_state);
  };

  const children_with_props = React.Children.map(children, (child, idx) => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement<ExpandableCardContentProps>(child)) {
      return React.cloneElement<ExpandableCardContentProps>(child, {
        ...child.props,
        is_expanded: expanded_state[idx],
        onClick: () => {
          if (child.props.onClick) {
            child.props.onClick();
          }
          handleOnClick(idx);
        },
        card_header_classes_override: header_classes_override
      });
    }
    return child;
  });

  return (
    <Card
      className={classes.container}
      classes={card_classes_override}
      {...otherProps}
    >
      {children_with_props}
    </Card>
  );
};
